import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useUI } from '../../context/UIContext';

const MobileWarehouseMap = ({ warehouseId, searchQuery = '' }) => {
    const { user } = useAuth();
    const { showToast, theme } = useUI();
    const [locations, setLocations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedElement, setSelectedElement] = useState(null);
    const scrollContainerRef = useRef(null);

    useEffect(() => {
        const fetchMapData = async () => {
            try {
                const res = await fetch(`/api/locations?warehouseId=${warehouseId}&companyId=${user.companyId}&includeProducts=true`);
                if (res.ok) setLocations(await res.json());
            } catch (error) {
                showToast("Erreur chargement carte", "error");
            } finally {
                setLoading(false);
            }
        };
        fetchMapData();
    }, [warehouseId, user.companyId, showToast]);

    const layout = useMemo(() => {
        if (locations.length === 0) return { zones: [], totalW: 100, totalH: 100 };

        const zones = locations.filter(l => l.type === 'Zone');
        const aisles = locations.filter(l => l.type === 'Aisle');
        const elements = locations.filter(l => l.type === 'Element');
        const slots = locations.filter(l => l.type === 'Slot');

        const query = searchQuery.toLowerCase().trim();

        // Mobile Layout Constants
        const ZONE_PAD = 30;
        const ZONE_GAP = 40;
        const AISLE_W = 120;
        const AISLE_GAP = 20;
        const AISLE_HEADER = 35;
        const ELEM_H = 70;
        const ELEM_GAP = 12;
        const SLOT_H = 22;
        const SLOT_GAP = 5;

        let layoutZones = [];
        let curY = 40;

        zones.forEach(zone => {
            const zoneAisles = aisles.filter(a => a.parentId === zone.id);
            let aisleX = ZONE_PAD;
            let maxAisleH = 60;
            let layoutAisles = [];

            zoneAisles.forEach(aisle => {
                const aisleElements = elements.filter(e => e.parentId === aisle.id);
                
                // Find longest text in this Aisle branch to determine dynamic width
                let maxChar = aisle.name.length;
                aisleElements.forEach(elem => {
                    if (elem.name.length > maxChar) maxChar = elem.name.length;
                    const elemSlots = slots.filter(s => s.parentId === elem.id);
                    elemSlots.forEach(slot => {
                        if (slot.name.length > maxChar) maxChar = slot.name.length;
                    });
                });
                
                const aisleW = Math.max(100, maxChar * 6.2 + 18);
                let elemY = AISLE_HEADER + 10;
                let layoutElements = [];

                aisleElements.forEach(elem => {
                    const elemSlots = slots.filter(s => s.parentId === elem.id);
                    const elemHeight = Math.max(ELEM_H, 35 + elemSlots.length * (SLOT_H + SLOT_GAP));

                    let layoutSlots = elemSlots.map((slot, si) => {
                        const slotHasProduct = query && (slot.products || []).some(p =>
                            (p.sku || '').toLowerCase().includes(query) ||
                            (p.name || '').toLowerCase().includes(query)
                        );
                        return {
                            ...slot,
                            rx: 6,
                            ry: 32 + si * (SLOT_H + SLOT_GAP),
                            rw: aisleW - 18,
                            rh: SLOT_H,
                            highlighted: !!slotHasProduct
                        };
                    });

                    const elemHasProduct = query && (
                        (elem.products || []).some(p =>
                            (p.sku || '').toLowerCase().includes(query) ||
                            (p.name || '').toLowerCase().includes(query)
                        ) ||
                        layoutSlots.some(s => s.highlighted)
                    );

                    layoutElements.push({
                        ...elem,
                        rx: 4,
                        ry: elemY,
                        rw: aisleW - 8,
                        rh: elemHeight,
                        slots: layoutSlots,
                        highlighted: !!elemHasProduct
                    });

                    elemY += elemHeight + ELEM_GAP;
                });

                const totalAisleH = Math.max(70, elemY + 10);
                if (totalAisleH > maxAisleH) maxAisleH = totalAisleH;

                layoutAisles.push({
                    ...aisle,
                    rx: aisleX,
                    ry: 50,
                    rw: aisleW,
                    rh: totalAisleH,
                    elements: layoutElements
                });

                aisleX += aisleW + AISLE_GAP;
            });

            const minW = Math.max(260, zone.name.length * 9.5 + 40);
            const zoneW = Math.max(minW, aisleX + ZONE_PAD);
            const zoneH = maxAisleH + 70;

            layoutZones.push({
                ...zone,
                rx: 20,
                ry: curY,
                rw: zoneW,
                rh: zoneH,
                aisles: layoutAisles
            });

            curY += zoneH + ZONE_GAP;
        });

        const totalW = Math.max(window.innerWidth - 32, Math.max(...layoutZones.map(z => z.rw + 40), 100));
        const totalH = Math.max(400, curY + 20);

        return { zones: layoutZones, totalW, totalH };
    }, [locations, searchQuery]);

    if (loading) return <div className="h-64 flex items-center justify-center text-slate-400 animate-pulse bg-slate-50 dark:bg-slate-900/50 rounded-2xl border border-dashed border-slate-200 dark:border-slate-800">Génération du plan...</div>;

    if (locations.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center text-slate-400 p-8 text-center bg-slate-50 dark:bg-slate-900/50 rounded-2xl border border-dashed border-slate-200 dark:border-slate-800">
                <span className="material-symbols-outlined text-3xl mb-3 text-slate-300">account_tree</span>
                <p className="text-sm font-bold">Aucun emplacement.</p>
            </div>
        );
    }

    const typeColors = {
        Zone: { fill: 'var(--tw-bg-slate-50)', stroke: '#cbd5e1', text: '#64748b' },
        Aisle: { fill: '#ffffff', stroke: '#e2e8f0', text: '#475569' },
        Element: { fill: '#eff6ff', stroke: '#bfdbfe', text: '#1e40af' },
        Slot: { fill: '#f0fdf4', stroke: '#bbf7d0', text: '#166534' }
    };

    return (
        <div className="flex flex-col gap-4">
            <div className="flex items-center justify-end px-1">
                {searchQuery && (
                    <div className="bg-rose-500 text-white px-2 py-1 rounded text-[9px] font-black animate-pulse flex items-center gap-1">
                        <span className="material-symbols-outlined text-[12px]">search</span>
                        {searchQuery}
                    </div>
                )}
            </div>

            <div
                ref={scrollContainerRef}
                className="relative w-full overflow-auto rounded-2xl border border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-950/30 touch-pan-x touch-pan-y max-h-[60vh]"
            >
                <svg
                    viewBox={`0 0 ${layout.totalW} ${layout.totalH}`}
                    width="100.1%"
                    height="auto"
                    className="transition-all duration-300"
                >
                    <defs>
                        <pattern id="grid-mobile" width="20" height="20" patternUnits="userSpaceOnUse">
                            <path d="M 20 0 L 0 0 0 20" fill="none" stroke="currentColor" strokeWidth="0.5" className="text-slate-200 dark:text-slate-800" opacity="0.3" />
                        </pattern>
                    </defs>
                    <rect width="100%" height="100%" fill="url(#grid-mobile)" />

                    {layout.zones.map(zone => (
                        <g key={zone.id}>
                            <rect
                                x={zone.rx} y={zone.ry} width={zone.rw} height={zone.rh}
                                rx="12"
                                fill={theme === 'dark' ? '#0f172a' : '#f1f5f9'}
                                stroke={typeColors.Zone.stroke}
                                strokeWidth="1"
                                strokeDasharray="4 2"
                            />
                            <text x={zone.rx + 12} y={zone.ry + 24} fontSize="14" fontWeight="900" fill={typeColors.Zone.text} className="dark:fill-slate-400">
                                {zone.name}
                            </text>

                            {zone.aisles.map(aisle => (
                                <g key={aisle.id} transform={`translate(${zone.rx + aisle.rx}, ${zone.ry + aisle.ry})`}>
                                    <rect
                                        x={0} y={0} width={aisle.rw} height={aisle.rh}
                                        rx="8"
                                        fill={theme === 'dark' ? '#1e293b' : '#ffffff'}
                                        stroke={typeColors.Aisle.stroke}
                                        strokeWidth="1"
                                    />
                                    <text x={aisle.rw / 2} y={22} fontSize="11" fontWeight="800" fill={typeColors.Aisle.text} className="dark:fill-slate-300" textAnchor="middle">
                                        {aisle.name}
                                    </text>

                                    {aisle.elements.map(elem => (
                                        <g
                                            key={elem.id}
                                            onClick={() => setSelectedElement(selectedElement?.id === elem.id ? null : elem)}
                                        >
                                            <rect
                                                x={elem.rx} y={elem.ry} width={elem.rw} height={elem.rh}
                                                rx="6"
                                                fill={elem.highlighted ? '#fef2f2' : selectedElement?.id === elem.id ? '#dbeafe' : theme === 'dark' ? '#334155' : typeColors.Element.fill}
                                                stroke={elem.highlighted ? '#ef4444' : selectedElement?.id === elem.id ? '#3b82f6' : typeColors.Element.stroke}
                                                strokeWidth={elem.highlighted ? 2.5 : 1}
                                            />
                                            <text x={elem.rx + elem.rw / 2} y={elem.ry - 4} fontSize="9" fontWeight="800" fill={elem.highlighted ? '#dc2626' : theme === 'dark' ? '#94a3b8' : typeColors.Element.text} textAnchor="middle">
                                                {elem.name}
                                            </text>

                                            {elem.slots.map(slot => (
                                                <g key={slot.id}>
                                                    <rect
                                                        x={elem.rx + slot.rx}
                                                        y={elem.ry + slot.ry}
                                                        width={slot.rw}
                                                        height={slot.rh}
                                                        rx="3"
                                                        fill={slot.highlighted ? '#fee2e2' : theme === 'dark' ? '#1e293b' : typeColors.Slot.fill}
                                                        stroke={slot.highlighted ? '#ef4444' : typeColors.Slot.stroke}
                                                        strokeWidth={slot.highlighted ? 1.5 : 0.5}
                                                    />
                                                    <text
                                                        x={elem.rx + slot.rx + slot.rw / 2}
                                                        y={elem.ry + slot.ry + 13}
                                                        fontSize="8"
                                                        fontWeight="700"
                                                        fill={theme === 'dark' ? '#cbd5e1' : typeColors.Slot.text}
                                                        textAnchor="middle"
                                                    >
                                                        {slot.name}
                                                    </text>
                                                </g>
                                            ))}
                                        </g>
                                    ))}
                                </g>
                            ))}
                        </g>
                    ))}
                </svg>
            </div>

            {/* Bottom Info Bar for selected Rack - Fixed Bottom Sheet */}
            {selectedElement && (
                <div className="fixed inset-0 z-[100] flex items-end justify-center animate-in fade-in duration-300">
                    <div
                        className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm ring-1 ring-white/10"
                        onClick={() => setSelectedElement(null)}
                    ></div>
                    <div className="relative w-full max-w-2xl bg-white dark:bg-slate-900 rounded-t-[2.5rem] border-t border-slate-200 dark:border-slate-800 shadow-2xl animate-in slide-in-from-bottom duration-500 max-h-[85vh] flex flex-col overflow-hidden pb- safe">
                        {/* Pull Bar */}
                        <div className="flex justify-center p-3 shrink-0">
                            <div className="w-12 h-1.5 bg-slate-200 dark:bg-slate-700 rounded-full"></div>
                        </div>

                        <div className="px-6 pb-4 flex justify-between items-start shrink-0">
                            <div>
                                <h4 className="font-black text-xl text-slate-900 dark:text-white uppercase tracking-tight">{selectedElement.name}</h4>
                                <p className="text-[10px] text-slate-500 font-bold uppercase tracking-wider">Contenu détaillé par emplacement</p>
                            </div>
                            <button
                                onClick={() => setSelectedElement(null)}
                                className="size-10 flex items-center justify-center rounded-full bg-slate-100 dark:bg-slate-800 text-slate-400 hover:text-rose-500 transition-colors"
                            >
                                <span className="material-symbols-outlined">close</span>
                            </button>
                        </div>

                        <div className="flex-1 overflow-y-auto px-6 py-2 space-y-6 custom-scrollbar pb-10">
                            {(() => {
                                const ownProducts = selectedElement.products || [];
                                const slotLocations = locations.filter(l => l.parentId === selectedElement.id && l.type === 'Slot');

                                const items = [];

                                // Products directly on Element
                                if (ownProducts.length > 0) {
                                    items.push(
                                        <div key="base" className="space-y-3">
                                            <div className="flex items-center gap-2">
                                                <div className="h-4 w-1 bg-primary rounded-full"></div>
                                                <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Sur le Rack principal</div>
                                            </div>
                                            <div className="grid gap-2">
                                                {ownProducts.map(p => (
                                                    <div key={p.id} className="flex items-center justify-between p-4 rounded-2xl bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-800">
                                                        <div className="flex flex-col">
                                                            <span className="text-sm font-bold text-slate-900 dark:text-white">{p.name}</span>
                                                            <span className="text-[10px] text-slate-500 font-mono">SKU: {p.sku}</span>
                                                        </div>
                                                        <span className="bg-primary text-white text-[11px] font-black px-3 py-1 rounded-full shadow-lg shadow-primary/20">{p.quantity}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    );
                                }

                                // Products in Slots
                                slotLocations.forEach(slot => {
                                    const slotProds = slot.products || [];
                                    if (slotProds.length > 0) {
                                        items.push(
                                            <div key={slot.id} className="space-y-3">
                                                <div className="flex items-center gap-2">
                                                    <div className="h-4 w-1 bg-emerald-500 rounded-full"></div>
                                                    <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest text-emerald-600/80">
                                                        Emplacement : {slot.name}
                                                    </div>
                                                </div>
                                                <div className="grid gap-2">
                                                    {slotProds.map(p => (
                                                        <div key={p.id} className="flex items-center justify-between p-4 rounded-2xl bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-800">
                                                            <div className="flex flex-col">
                                                                <span className="text-sm font-bold text-slate-900 dark:text-white">{p.name}</span>
                                                                <span className="text-[10px] text-slate-500 font-mono">SKU: {p.sku}</span>
                                                            </div>
                                                            <span className="bg-emerald-500 text-white text-[11px] font-black px-3 py-1 rounded-full shadow-lg shadow-emerald-500/20">{p.quantity}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        );
                                    }
                                });

                                if (items.length === 0) return (
                                    <div className="flex flex-col items-center justify-center py-12 text-slate-300">
                                        <span className="material-symbols-outlined text-5xl mb-3 opacity-20">inventory</span>
                                        <p className="text-xs font-bold uppercase tracking-widest opacity-50 italic">Aucun produit dans ce rack</p>
                                    </div>
                                );

                                return items;
                            })()}
                        </div>
                    </div>
                </div>
            )}

            <div className="bg-slate-100/50 dark:bg-slate-900/50 rounded-xl p-3 flex justify-around items-center">
                <div className="flex items-center gap-1.5 opacity-70">
                    <div className="w-2 h-2 rounded-full border border-slate-300 bg-slate-50"></div>
                    <span className="text-[9px] font-black uppercase text-slate-500">Zones</span>
                </div>
                <div className="flex items-center gap-1.5 opacity-70">
                    <div className="w-2 h-2 rounded-full border border-blue-300 bg-blue-50"></div>
                    <span className="text-[9px] font-black uppercase text-slate-500">Racks</span>
                </div>
                <div className="flex items-center gap-1.5 opacity-70">
                    <div className="w-2 h-2 rounded-full border border-green-300 bg-green-50"></div>
                    <span className="text-[9px] font-black uppercase text-slate-500">Emplts</span>
                </div>
            </div>
        </div>
    );
};

export default MobileWarehouseMap;
